import { ReactElement, useMemo, useRef, useState, MouseEvent } from 'react';
import { OpcBinStats } from '../../services/opc-bin.service';
import {
  CARD_CLASS,
  CONC_UNIT,
  OPC_BIN_COUNT,
  binRangeLabel,
  fmtConc,
  toggleClass,
  useElementWidth,
  binColor,
  sliceColor
} from './particleBinShared';

const MARGIN = { top: 10, right: 16, bottom: 66, left: 64 };
const PLOT_HEIGHT = 300;
const MIN_LOG_FLOOR = 1e-4;

type BinBoxPlotProps = {
  stats: OpcBinStats[];
  boundaries: number[];
  /** Concentration per bin at each time picked on the contour plot. */
  overlays?: { timestamp: number; values: (number | null)[] }[];
  /** Channels in the distribution. Defaults to the OPC-N3's 24. */
  binCount?: number;
  /** Unit label for the plotted quantity. Defaults to the OPC-N3's #/ml. */
  unit?: string;
  /** Card heading and the line under it. */
  title?: string;
  subtitle?: string;
};

/**
 * Size-distribution box plot: one box-and-whisker per OPC-N3 bin summarizing
 * the concentration statistics over the selected time period. Whiskers span
 * p05–p95, the box p25–p75, with the median line and a mean dot.
 * @param props - Per-bin stats.
 * @returns Box plot card.
 */
export default function BinBoxPlot(props: BinBoxPlotProps): ReactElement {
  const {
    stats,
    boundaries,
    overlays = [],
    binCount = OPC_BIN_COUNT,
    unit = CONC_UNIT,
    title = 'Size Distribution',
    subtitle
  } = props;
  const [logScale, setLogScale] = useState(true);
  const [hoverBin, setHoverBin] = useState<{ b: number; x: number; y: number } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const width = useElementWidth(containerRef);

  const plotW = Math.max(width - MARGIN.left - MARGIN.right, 50);
  const height = PLOT_HEIGHT + MARGIN.top + MARGIN.bottom;
  const slotW = plotW / binCount;
  const boxW = Math.min(slotW * 0.55, 34);

  const { floor, maxValue } = useMemo(() => {
    let max = 0;
    let minPositive = Infinity;
    stats.forEach((s) => {
      [s.p05, s.p25, s.p50, s.p75, s.p95, s.mean, s.max].forEach((v) => {
        if (v !== null && v !== undefined && Number.isFinite(v)) {
          if (v > max) max = v;
          if (v > 0 && v < minPositive) minPositive = v;
        }
      });
    });
    overlays.forEach((o) => {
      o.values.forEach((v) => {
        if (v !== null && v !== undefined && Number.isFinite(v)) {
          if (v > max) max = v;
          if (v > 0 && v < minPositive) minPositive = v;
        }
      });
    });
    const f = Number.isFinite(minPositive) ? Math.max(minPositive / 1.5, MIN_LOG_FLOOR) : MIN_LOG_FLOOR;
    return { floor: f, maxValue: max };
  }, [stats, overlays]);

  const yOf = useMemo(() => {
    const domainMax = maxValue > 0 ? maxValue * 1.15 : 1;
    if (!logScale) {
      return (v: number): number => PLOT_HEIGHT - (Math.max(v, 0) / domainMax) * PLOT_HEIGHT;
    }
    const logMin = Math.log10(floor);
    const logMax = Math.log10(Math.max(domainMax, floor * 10));
    return (v: number): number => {
      const clamped = Math.max(v, floor);
      return PLOT_HEIGHT - ((Math.log10(clamped) - logMin) / (logMax - logMin)) * PLOT_HEIGHT;
    };
  }, [logScale, floor, maxValue]);

  const yTicks = useMemo(() => {
    if (!logScale) {
      const domainMax = maxValue > 0 ? maxValue * 1.15 : 1;
      return Array.from({ length: 6 }, (_, i) => (domainMax * i) / 5);
    }
    const ticks: number[] = [];
    const start = Math.floor(Math.log10(floor));
    const end = Math.ceil(Math.log10(Math.max(maxValue, floor * 10)));
    for (let p = start; p <= end; p += 1) ticks.push(10 ** p);
    return ticks;
  }, [logScale, floor, maxValue]);

  /**
   * Pointer → bin slot for the shared tooltip.
   * @param e - Mouse event over the SVG.
   */
  const onMove = (e: MouseEvent<SVGSVGElement>): void => {
    const rect = e.currentTarget.getBoundingClientRect();
    const px = e.clientX - rect.left - MARGIN.left;
    const py = e.clientY - rect.top - MARGIN.top;
    if (px < 0 || py < 0 || px >= plotW || py >= PLOT_HEIGHT) {
      setHoverBin(null);
      return;
    }
    const b = Math.min(Math.floor(px / slotW), binCount - 1);
    setHoverBin({ b, x: e.clientX - rect.left, y: e.clientY - rect.top });
  };

  const hovered = hoverBin ? stats[hoverBin.b] : null;
  const hasData = stats.some((s) => s.count > 0);

  // Clicked-time overlays: for each picked time, the bins that have a value.
  const overlaySeries = useMemo(
    () =>
      overlays.map((o) => {
        const pts: { b: number; v: number }[] = [];
        o.values.forEach((v, b) => {
          if (b < binCount && v !== null && v !== undefined && Number.isFinite(v)) {
            pts.push({ b, v });
          }
        });
        return { timestamp: o.timestamp, points: pts };
      }),
    [overlays, binCount]
  );

  return (
    <div className={CARD_CLASS}>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-base font-semibold text-gray-100">{title}</h2>
          <p className="text-xs text-gray-400">
            {subtitle ??
              'Box plot of concentration per particle size over the selected period'}{' '}
            · whiskers p05–p95, box p25–p75, line = median, dot = mean
            {overlaySeries.length === 1 && (
              <>
                {' '}
                · dashed line = {new Date(overlaySeries[0].timestamp).toLocaleString()}
              </>
            )}
            {overlaySeries.length > 1 && (
              <>
                {' '}
                · {overlaySeries.length} dashed lines = times picked on the contour plot
              </>
            )}
          </p>
        </div>
        <button type="button" className={toggleClass(logScale)} onClick={() => setLogScale(!logScale)}>
          Log scale
        </button>
      </div>
      <div ref={containerRef} className="relative w-full">
        {!hasData ? (
          <p className="py-10 text-center text-sm text-gray-500">No data in the selected range.</p>
        ) : (
          width > 0 && (
            <svg
              width={width}
              height={height}
              onMouseMove={onMove}
              onMouseLeave={() => setHoverBin(null)}
              role="img"
              aria-label="OPC bin size distribution box plot"
            >
              <g transform={`translate(${MARGIN.left},${MARGIN.top})`}>
                {/* Gridlines + y labels */}
                {yTicks.map((tick) => (
                  <g key={tick} transform={`translate(0,${yOf(tick)})`}>
                    <line x2={plotW} stroke="rgba(107,114,128,0.15)" />
                    <text x={-8} y={3} textAnchor="end" fontSize={9} fill="#9ca3af">
                      {fmtConc(tick)}
                    </text>
                  </g>
                ))}
                <text
                  transform={`translate(${-MARGIN.left + 12},${PLOT_HEIGHT / 2}) rotate(-90)`}
                  textAnchor="middle"
                  fontSize={11}
                  fill="#9ca3af"
                >
                  Concentration ({unit})
                </text>
                {/* Boxes */}
                {stats.map((s, b) => {
                  if (s.count === 0 || s.p50 === null) return null;
                  const cx = b * slotW + slotW / 2;
                  const color = binColor(b / (binCount - 1));
                  const p05 = s.p05 ?? s.p25 ?? s.p50;
                  const p25 = s.p25 ?? s.p50;
                  const p75 = s.p75 ?? s.p50;
                  const p95 = s.p95 ?? s.p75 ?? s.p50;
                  const yP05 = yOf(p05 as number);
                  const yP25 = yOf(p25 as number);
                  const yP50 = yOf(s.p50);
                  const yP75 = yOf(p75 as number);
                  const yP95 = yOf(p95 as number);
                  return (
                    <g key={s.bin}>
                      {/* Whisker */}
                      <line x1={cx} x2={cx} y1={yP95} y2={yP05} stroke={color} strokeWidth={1} />
                      <line x1={cx - boxW / 4} x2={cx + boxW / 4} y1={yP95} y2={yP95} stroke={color} strokeWidth={1} />
                      <line x1={cx - boxW / 4} x2={cx + boxW / 4} y1={yP05} y2={yP05} stroke={color} strokeWidth={1} />
                      {/* Box */}
                      <rect
                        x={cx - boxW / 2}
                        y={yP75}
                        width={boxW}
                        height={Math.max(yP25 - yP75, 1)}
                        fill={color}
                        fillOpacity={0.35}
                        stroke={color}
                        strokeWidth={1}
                        rx={1}
                      />
                      {/* Median */}
                      <line
                        x1={cx - boxW / 2}
                        x2={cx + boxW / 2}
                        y1={yP50}
                        y2={yP50}
                        stroke="#f9fafb"
                        strokeWidth={1.4}
                      />
                      {/* Mean */}
                      {s.mean !== null && (
                        <circle cx={cx} cy={yOf(s.mean)} r={2.2} fill="#f9fafb" fillOpacity={0.9} />
                      )}
                    </g>
                  );
                })}
                {/* Clicked-time overlays from the contour plot, colour-matched to it */}
                {overlaySeries.map((series, i) => (
                  <g key={series.timestamp}>
                    <path
                      d={series.points
                        .map((p, j) => {
                          const cx = p.b * slotW + slotW / 2;
                          return `${j === 0 ? 'M' : 'L'}${cx.toFixed(1)},${yOf(p.v).toFixed(1)}`;
                        })
                        .join('')}
                      fill="none"
                      stroke={sliceColor(i)}
                      strokeWidth={1.3}
                      strokeDasharray="4 3"
                      opacity={0.95}
                    />
                    {series.points.map((p) => (
                      <circle
                        key={p.b}
                        cx={p.b * slotW + slotW / 2}
                        cy={yOf(p.v)}
                        r={1.9}
                        fill={sliceColor(i)}
                      />
                    ))}
                  </g>
                ))}
                {/* Hover slot highlight */}
                {hoverBin && (
                  <rect
                    x={hoverBin.b * slotW}
                    width={slotW}
                    height={PLOT_HEIGHT}
                    fill="#f9fafb"
                    fillOpacity={0.05}
                  />
                )}
                {/* X labels: particle size ranges */}
                {stats.map((s, b) => (
                  <text
                    key={s.bin}
                    transform={`translate(${b * slotW + slotW / 2},${PLOT_HEIGHT + 10}) rotate(-55)`}
                    textAnchor="end"
                    fontSize={8.5}
                    fill="#9ca3af"
                  >
                    {binRangeLabel(boundaries, b)}
                  </text>
                ))}
                <text
                  x={plotW / 2}
                  y={PLOT_HEIGHT + MARGIN.bottom - 6}
                  textAnchor="middle"
                  fontSize={11}
                  fill="#9ca3af"
                >
                  Particle size (µm)
                </text>
              </g>
            </svg>
          )
        )}
        {hovered && hoverBin && (
          <div
            className="pointer-events-none absolute z-10 rounded border border-gray-700 bg-background-700 px-2.5 py-1.5 text-xs text-gray-100 shadow-lg"
            style={{
              left: Math.min(hoverBin.x + 12, Math.max(width - 200, 0)),
              top: Math.max(hoverBin.y - 90, 0)
            }}
          >
            <div className="mb-0.5 font-semibold">
              Bin {hovered.bin} · {binRangeLabel(boundaries, hovered.bin)} µm
            </div>
            {hovered.count === 0 ? (
              <div className="text-gray-400">no data</div>
            ) : (
              <table className="text-[11px]">
                <tbody>
                  <tr><td className="pr-2 text-gray-400">median</td><td>{fmtConc(hovered.p50)}</td></tr>
                  <tr><td className="pr-2 text-gray-400">p25–p75</td><td>{fmtConc(hovered.p25)} – {fmtConc(hovered.p75)}</td></tr>
                  <tr><td className="pr-2 text-gray-400">p05–p95</td><td>{fmtConc(hovered.p05)} – {fmtConc(hovered.p95)}</td></tr>
                  <tr><td className="pr-2 text-gray-400">mean</td><td>{fmtConc(hovered.mean)}</td></tr>
                  <tr><td className="pr-2 text-gray-400">min / max</td><td>{fmtConc(hovered.min)} / {fmtConc(hovered.max)}</td></tr>
                  <tr><td className="pr-2 text-gray-400">samples</td><td>{hovered.count}</td></tr>
                  {overlaySeries.map((o, i) => (
                    <tr key={o.timestamp}>
                      <td className="pr-2 text-gray-400">
                        <span
                          className="mr-1 inline-block h-2 w-2 rounded-full align-middle"
                          style={{ backgroundColor: sliceColor(i) }}
                        />
                        {new Date(o.timestamp).toLocaleTimeString()}
                      </td>
                      <td>{fmtConc(overlays[i]?.values[hovered.bin])}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
